import React, { useState, useEffect } from "react";
import { Button, PageSpinner, Icon } from "@nike/epic-react-ui";
import {
    NikeDesignSystemProvider,
    TextFieldStyled,
    TextStyled,
    DropDownStyled
} from "@nike/nike-design-system-components";
import { RadioGroup, Radio } from "@nike/eds";
import "./LgStatus.css";

function OlpnTable({ items }) {
    if (!items || items.length === 0) {
        return (
            <div className="lg-olpn-empty">
                <NikeDesignSystemProvider>
                    <TextStyled appearance="body2" style={{ color: '#757575' }}>
                        No OLPNs found
                    </TextStyled>
                </NikeDesignSystemProvider>
            </div>
        );
    }

    return (
        <table className="lg-olpn-table">
            <thead>
                <tr>
                    <th>OLPN</th>
                    <th>DC Order ID</th>
                    <th>Diverted</th>
                    <th>GTP DateTime</th>
                    <th>Modified On</th>
                </tr>
            </thead>
            <tbody>
                {items.map((olpn, idx) => (
                    <tr key={olpn.olpn || idx}>
                        <td className="lg-olpn-id">{olpn.olpn}</td>
                        <td>{olpn.dc_order_id}</td>
                        <td>
                            <span className={`lg-divert-badge ${olpn.diverted === 'Y' ? 'lg-diverted-yes' : 'lg-diverted-no'}`}>
                                {olpn.diverted === 'Y' ? 'Y' : 'N'}
                            </span>
                        </td>
                        <td>{olpn.gtp_datetime || '-'}</td>
                        <td>{olpn.modified_on || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const PAGE_SIZE_OPTIONS = [
    { label: "10", value: "10" },
    { label: "25", value: "25" },
    { label: "50", value: "50" },
    { label: "100", value: "100" },
    { label: "200", value: "200" }
];

function LgCard({ lg, onFetchOlpnPage, olpnSize, onOlpnSizeChange }) {
    const [expanded, setExpanded] = useState(false);
    const [activeOlpnTab, setActiveOlpnTab] = useState('diverted');
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [olpnSearch, setOlpnSearch] = useState('');

    const divertedTotal = lg.diverted_olpns?.total_items ?? 0;
    const nonDivertedTotal = lg.non_diverted_olpns?.total_items ?? 0;
    const totalCount = lg.total_olpns || 0;
    const progressPct = totalCount > 0 ? (divertedTotal / totalCount) * 100 : 0;


    const currentPage = lg.diverted_olpns?.page ?? 0;
    const pageSize = olpnSize || 10;
    const totalPages = Math.max(
        Math.ceil(divertedTotal / pageSize) || 1,
        Math.ceil(nonDivertedTotal / pageSize) || 1
    );

    useEffect(() => {
        setIsLoadingPage(false);
        setOlpnSearch('');
    }, [lg.diverted_olpns?.page, lg.non_diverted_olpns?.page, olpnSize]);

    const handlePageNav = (newPage) => {
        setIsLoadingPage(true);
        onFetchOlpnPage(lg.loading_group_id, newPage);
    };


    const searchTerm = olpnSearch.trim().toLowerCase();
    const isSearching = searchTerm.length > 0;

    const allDiverted = (lg.diverted_olpns?.items || []).map(o => ({ ...o, _type: 'diverted' }));
    const allNonDiverted = (lg.non_diverted_olpns?.items || []).map(o => ({ ...o, _type: 'non_diverted' }));
    const allItems = [...allDiverted, ...allNonDiverted];

    const searchResults = isSearching
        ? allItems.filter(o =>
            (o.olpn || '').toLowerCase().includes(searchTerm) ||
            (o.dc_order_id || '').toLowerCase().includes(searchTerm)
        )
        : [];

    return (
        <div className="lg-card">
            <div className="lg-card-header">
                <div className="lg-card-title-row">
                    <NikeDesignSystemProvider>
                        <TextStyled appearance="body1 text-bold">
                            {lg.loading_group_id}
                        </TextStyled>
                    </NikeDesignSystemProvider>
                    <div className="lg-card-badges">
                        <span className={`lg-status-badge ${
                            lg.loading_group_status === 'Assigned' ? 'lg-badge-assigned' : 'lg-badge-unassigned'
                        }`}>
                            {lg.loading_group_status}
                        </span>
                        <span className={`lg-final-plan-badge ${lg.final_plan === 'Y' ? 'lg-plan-yes' : 'lg-plan-no'}`}>
                            Final Plan: {lg.final_plan === 'Y' ? 'Y' : 'N'}
                        </span>
                    </div>
                </div>

                <div className="lg-card-details">
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Carrier</span>
                        <span className="lg-detail-value">{lg.carrier_id || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Carrier Hub</span>
                        <span className="lg-detail-value">{lg.carrier_hub || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Cutoff</span>
                        <span className="lg-detail-value">{lg.cutoff_date_time || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Trailer</span>
                        <span className="lg-detail-value">{lg.trailer_id || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Spur</span>
                        <span className="lg-detail-value">{lg.assigned_spur || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Prev Spur</span>
                        <span className="lg-detail-value">{lg.previous_spur || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Type</span>
                        <span className="lg-detail-value">{lg.loading_group_type || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Carrier Load Type</span>
                        <span className="lg-detail-value">{lg.carrierload_type_code || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Service Level</span>
                        <span className="lg-detail-value">{lg.service_level_code || '-'}</span>
                    </div>
                    <div className="lg-detail-item">
                        <span className="lg-detail-label">Pallet Required</span>
                        <span className="lg-detail-value">{lg.pallet_required || '-'}</span>
                    </div>
                </div>

                <div className="lg-progress-row">
                    <div className="lg-progress-bar-container">
                        <div className="lg-progress-bar" style={{ width: `${progressPct}%` }} />
                    </div>
                    <NikeDesignSystemProvider>
                        <TextStyled appearance="body2" style={{ whiteSpace: 'nowrap' }}>
                            {divertedTotal} / {totalCount} diverted
                        </TextStyled>
                    </NikeDesignSystemProvider>
                    <span className={`lg-complete-badge ${lg.can_complete ? 'lg-can-complete' : 'lg-cannot-complete'}`}>
                        {lg.can_complete ? 'Can Complete' : 'Cannot Complete'}
                    </span>
                </div>
            </div>

            <div className="lg-card-expand" onClick={() => setExpanded(!expanded)}>
                <Icon type={expanded ? "CaretDown" : "CaretRight"} size="s" />
                <NikeDesignSystemProvider>
                    <TextStyled appearance="body2 text-bold">
                        OLPNs — Diverted: {divertedTotal}&nbsp;|&nbsp;Non-Diverted: {nonDivertedTotal}&nbsp;|&nbsp;Total: {totalCount}
                    </TextStyled>
                </NikeDesignSystemProvider>
            </div>

            {expanded && (
                <div className="lg-olpn-section">
                    {/* OLPN search bar */}
                    <div className="lg-olpn-search-bar">
                        <NikeDesignSystemProvider>
                            <TextFieldStyled
                                label="Search OLPN / DC Order ID"
                                value={olpnSearch}
                                onChange={(e) => setOlpnSearch(e.target.value)}
                                className="lg-olpn-search-field"
                            />
                        </NikeDesignSystemProvider>
                        {isSearching && (
                            <button className="lg-olpn-search-clear" onClick={() => setOlpnSearch('')}>✕</button>
                        )}
                    </div>

                    {isLoadingPage ? (
                        <div className="lg-olpn-loading">Loading OLPNs…</div>
                    ) : isSearching ? (
                        /* ── Search results view ── */
                        <>
                            {searchResults.length > 0 ? (
                                <>
                                    <div className="lg-olpn-search-summary">
                                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &quot;{olpnSearch}&quot; on page {currentPage + 1}
                                    </div>
                                    <table className="lg-olpn-table">
                                        <thead>
                                            <tr>
                                                <th>OLPN</th>
                                                <th>DC Order ID</th>
                                                <th>Diverted</th>
                                                <th>GTP DateTime</th>
                                                <th>Modified On</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map((olpn, idx) => (
                                                <tr key={olpn.olpn || idx}>
                                                    <td className="lg-olpn-id">{olpn.olpn}</td>
                                                    <td>{olpn.dc_order_id}</td>
                                                    <td>
                                                        <span className={`lg-divert-badge ${olpn.diverted === 'Y' ? 'lg-diverted-yes' : 'lg-diverted-no'}`}>
                                                            {olpn.diverted === 'Y' ? 'Y' : 'N'}
                                                        </span>
                                                    </td>
                                                    <td>{olpn.gtp_datetime || '-'}</td>
                                                    <td>{olpn.modified_on || '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            ) : (
                                <div className="lg-olpn-not-found">
                                    <span>No OLPN matching &quot;{olpnSearch}&quot; found on page {currentPage + 1}</span>
                                    {totalPages > 1 && (
                                        <span className="lg-olpn-not-found-hint">
                                            This search only covers the current page. Use ‹ Prev / Next › to check other pages.
                                        </span>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        /* ── Normal tab view ── */
                        <>
                            <div className="lg-olpn-tabs">
                                <button
                                    className={`lg-olpn-tab ${activeOlpnTab === 'diverted' ? 'lg-olpn-tab-active' : ''}`}
                                    onClick={() => setActiveOlpnTab('diverted')}
                                >
                                    Diverted ({divertedTotal})
                                </button>
                                <button
                                    className={`lg-olpn-tab ${activeOlpnTab === 'non_diverted' ? 'lg-olpn-tab-active' : ''}`}
                                    onClick={() => setActiveOlpnTab('non_diverted')}
                                >
                                    Non-Diverted ({nonDivertedTotal})
                                </button>
                            </div>

                            {activeOlpnTab === 'diverted'
                                ? <OlpnTable items={lg.diverted_olpns?.items || []} />
                                : <OlpnTable items={lg.non_diverted_olpns?.items || []} />
                            }
                        </>
                    )}

                    {/* Unified footer: page-size selector (always) + Prev/Next (when multi-page) */}
                    <div className="lg-olpn-footer">
                        <div className="lg-olpn-footer-size">
                            <NikeDesignSystemProvider>
                                <div style={{ width: '180px' }}>
                                    <DropDownStyled
                                        placeholder="Per page"
                                        value={String(olpnSize)}
                                        options={PAGE_SIZE_OPTIONS}
                                        onChange={(event) => onOlpnSizeChange(event.target.value)}
                                    />
                                </div>
                            </NikeDesignSystemProvider>
                        </div>

                        <div className="lg-olpn-pagination">
                            <button
                                className="lg-page-btn"
                                onClick={() => handlePageNav(currentPage - 1)}
                                disabled={currentPage === 0 || isLoadingPage}
                            >
                                ‹ Prev
                            </button>
                            <span className="lg-page-info">
                                Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                                className="lg-page-btn"
                                onClick={() => handlePageNav(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1 || isLoadingPage}
                            >
                                Next ›
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

class LgStatus extends React.Component {
    render() {
        const {
            showSpinner,
            searchValue,
            searchType,
            olpnSize,
            onSearchValueChange,
            onSearchTypeChange,
            onOlpnSizeChange,
            handleSearch,
            onFetchOlpnPage,
            statusData,
            errorMessage,
            dcValue
        } = this.props;


        return (
            <div className="lg-status-container">
                <PageSpinner show={showSpinner} />

                <NikeDesignSystemProvider>
                    <div className="lg-status-header">
                        <TextFieldStyled
                            value={searchValue}
                            onChange={(event) => onSearchValueChange(event.target.value)}
                            type="text"
                            placeholder={searchType === 'shipmentId' ? 'Enter Shipment ID' : 'Enter Loading Group ID'}
                            label={searchType === 'shipmentId' ? 'Shipment ID' : 'Loading Group ID'}
                            className="lg-search-input"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                        />
                        <div className="lg-search-type">
                            <RadioGroup
                                id="lg-search-type-group"
                                label="Search by"
                                name="searchType"
                                valueSelected={searchType}
                                onChange={(e) => onSearchTypeChange(e.target.value)}
                                orientation="horizontal"
                            >
                                <Radio label="Shipment ID" value="shipmentId" id="radio-shipmentId" />
                                <Radio label="LG ID" value="lgId" id="radio-lgId" />
                            </RadioGroup>
                        </div>
                        {dcValue && (
                            <div className="lg-dc-badge">
                                <Icon type="LocationOn" size="s" />
                                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                                    DC: {dcValue}
                                </span>
                            </div>
                        )}
                        <Button
                            type="NikeApp"
                            className="lg-search-button"
                            onClick={handleSearch}
                            disabled={!searchValue || searchValue.trim().length === 0}
                        >
                            <Icon type="Search" size="s" />
                            Search
                        </Button>
                    </div>
                </NikeDesignSystemProvider>

                {errorMessage && (
                    <div className="lg-error-message">
                        <Icon type="ErrorOutline" size="s" />
                        <NikeDesignSystemProvider>
                            <TextStyled appearance="body2 text-bold">Error: </TextStyled>
                            <TextStyled appearance="body2">{errorMessage}</TextStyled>
                        </NikeDesignSystemProvider>
                    </div>
                )}

                {statusData && (
                    <div className="lg-results">
                        <div className="lg-shipment-header">
                            <NikeDesignSystemProvider>
                                <TextStyled appearance="title3 text-bold">
                                    {statusData.shipment_id
                                        ? `Shipment: ${statusData.shipment_id}`
                                        : `Loading Group: ${statusData.loading_group_id}`}
                                </TextStyled>
                            </NikeDesignSystemProvider>
                            <NikeDesignSystemProvider>
                                <TextStyled appearance="body2" style={{ color: '#757575' }}>
                                    {statusData.loading_groups?.length || 0} loading group(s) found
                                </TextStyled>
                            </NikeDesignSystemProvider>
                        </div>

                        {statusData.loading_groups && statusData.loading_groups.length > 0 ? (
                            statusData.loading_groups.map((lg, idx) => (
                                <LgCard
                                    key={lg.loading_group_id || idx}
                                    lg={lg}
                                    onFetchOlpnPage={onFetchOlpnPage}
                                    olpnSize={olpnSize}
                                    onOlpnSizeChange={onOlpnSizeChange}
                                />
                            ))
                        ) : (
                            <div className="lg-no-groups">
                                <NikeDesignSystemProvider>
                                    <TextStyled appearance="body1" style={{ color: '#757575' }}>
                                        No loading groups found for this search
                                    </TextStyled>
                                </NikeDesignSystemProvider>
                            </div>
                        )}
                    </div>
                )}

                {!statusData && !errorMessage && (
                    <div className="lg-empty-state">
                        <Icon type="Search" size="l" style={{ color: '#BDBDBD' }} />
                        <NikeDesignSystemProvider>
                            <TextStyled appearance="body1" style={{ color: '#757575' }}>
                                Enter a Shipment ID or Loading Group ID above and click Search
                            </TextStyled>
                        </NikeDesignSystemProvider>
                    </div>
                )}
            </div>
        );
    }
}

export default LgStatus;
